// components/ClassesDashboard.js
import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import "./ClassesDashboard.css";

function ClassesDashboard() {
  const [classes, setClasses] = useState([]);
  const [editingClass, setEditingClass] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    schedule: "",
    description: "",
    level: "",
    price: "",
    duration: "",
    instructor: "",
    instructorBio: "",
    instructorImage: "",
    image: "", // For CSS class
    backgroundImage: "", // For custom background
    whatToBring: "",
    gallery: [],
  });
  const [loading, setLoading] = useState(false);
  const [siteSettings, setSiteSettings] = useState({
    showClasses: true,
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [galleryInput, setGalleryInput] = useState("");

  useEffect(() => {
    fetchClasses();
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const settingsRef = doc(db, "siteSettings", "global");
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        setSiteSettings(settingsSnap.data());
      }
    } catch (err) {
      console.error("Error fetching site settings:", err);
    }
  };

  const toggleClassesVisibility = async () => {
    try {
      setSavingSettings(true);
      const newValue = !siteSettings.showClasses;

      const settingsRef = doc(db, "siteSettings", "global");
      await updateDoc(settingsRef, {
        showClasses: newValue,
        updatedAt: new Date().toISOString(),
      });

      setSiteSettings((prev) => ({
        ...prev,
        showClasses: newValue,
      }));

      alert(
        `Sección de clases ${newValue ? "activada" : "desactivada"} correctamente.`,
      );
    } catch (err) {
      console.error("Error toggling classes visibility:", err);
      alert(
        "Error al cambiar la visibilidad de clases. Por favor intente nuevamente.",
      );
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchClasses = async () => {
    setLoading(true);
    const querySnapshot = await getDocs(collection(db, "classes"));
    const classesList = [];
    querySnapshot.forEach((doc) => {
      classesList.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    // Sort alphabetically by title
    setClasses(
      classesList.sort((a, b) => a.title?.localeCompare(b.title || "")),
    );
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleGalleryInput = (e) => {
    setGalleryInput(e.target.value);
  };

  const addGalleryImage = () => {
    if (galleryInput.trim() === "") return;

    setFormData({
      ...formData,
      gallery: [...(formData.gallery || []), galleryInput.trim()],
    });
    setGalleryInput("");
  };

  const removeGalleryImage = (index) => {
    const newGallery = [...formData.gallery];
    newGallery.splice(index, 1);
    setFormData({
      ...formData,
      gallery: newGallery,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.schedule) {
      alert("Por favor complete título y horario");
      return;
    }

    const classData = {
      ...formData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Clean up empty fields
    Object.keys(classData).forEach((key) => {
      if (
        classData[key] === "" ||
        (Array.isArray(classData[key]) && classData[key].length === 0)
      ) {
        delete classData[key];
      }
    });

    try {
      if (editingClass) {
        const classRef = doc(db, "classes", editingClass.id);
        await updateDoc(classRef, {
          ...classData,
          updatedAt: new Date().toISOString(),
        });
        setEditingClass(null);
      } else {
        await addDoc(collection(db, "classes"), classData);
      }

      setFormData({
        title: "",
        schedule: "",
        description: "",
        level: "",
        price: "",
        duration: "",
        instructor: "",
        instructorBio: "",
        instructorImage: "",
        image: "",
        backgroundImage: "",
        whatToBring: "",
        gallery: [],
      });
      setGalleryInput("");
      fetchClasses();
    } catch (error) {
      console.error("Error saving class:", error);
      alert("Error al guardar la clase. Por favor intente nuevamente.");
    }
  };

  const handleEdit = (cls) => {
    setEditingClass(cls);
    setFormData({
      title: cls.title || "",
      schedule: cls.schedule || "",
      description: cls.description || "",
      level: cls.level || "",
      price: cls.price || "",
      duration: cls.duration || "",
      instructor: cls.instructor || "",
      instructorBio: cls.instructorBio || "",
      instructorImage: cls.instructorImage || "",
      image: cls.image || "",
      backgroundImage: cls.backgroundImage || "",
      whatToBring: cls.whatToBring || "",
      gallery: cls.gallery || [],
    });
  };

  const handleDelete = async (classId) => {
    if (window.confirm("¿Está seguro de que desea eliminar esta clase?")) {
      try {
        await deleteDoc(doc(db, "classes", classId));
        fetchClasses();
      } catch (error) {
        console.error("Error deleting class:", error);
        alert("Error al eliminar la clase. Por favor intente nuevamente.");
      }
    }
  };

  const handleCancel = () => {
    setEditingClass(null);
    setFormData({
      title: "",
      schedule: "",
      description: "",
      level: "",
      price: "",
      duration: "",
      instructor: "",
      instructorBio: "",
      instructorImage: "",
      image: "",
      backgroundImage: "",
      whatToBring: "",
      gallery: [],
    });
    setGalleryInput("");
  };

  return (
    <div className="classes-dashboard">
      <div className="dashboard-header">
        <h1>Administración de Clases</h1>
        <p>Agregar, editar o eliminar clases para Espacio Paradigmas</p>

        {/* Classes Section Toggle */}
        <div className="section-toggle">
          <div className="toggle-label">
            <span className="toggle-title">
              Visibilidad de Clases en el Sitio
            </span>
            <span className="toggle-status">
              {siteSettings.showClasses ? "Activada" : "Desactivada"}
            </span>
          </div>
          <button
            onClick={toggleClassesVisibility}
            disabled={savingSettings}
            className={`visibility-toggle ${siteSettings.showClasses ? "active" : "inactive"}`}
          >
            {savingSettings ? (
              <span className="toggle-spinner"></span>
            ) : siteSettings.showClasses ? (
              <>
                <span className="toggle-icon">👁️</span>
                <span className="toggle-text">Ocultar Sección</span>
              </>
            ) : (
              <>
                <span className="toggle-icon">👁️‍🗨️</span>
                <span className="toggle-text">Mostrar Sección</span>
              </>
            )}
          </button>
          <p className="toggle-description">
            {siteSettings.showClasses
              ? "Las clases son visibles en la página de clases, página de inicio y navegación."
              : "Las clases están ocultas del sitio público (solo visibles en este panel)."}
          </p>
        </div>
      </div>

      <div className="dashboard-content">
        {/* Class Form */}
        <div className="class-form-section">
          <h2>{editingClass ? "Editar Clase" : "Agregar Nueva Clase"}</h2>

          <form onSubmit={handleSubmit} className="class-form">
            <div className="form-row">
              <div className="form-group">
                <label>Título *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Título de la clase"
                  required
                />
              </div>

              <div className="form-group">
                <label>Horario *</label>
                <input
                  type="text"
                  name="schedule"
                  value={formData.schedule}
                  onChange={handleInputChange}
                  placeholder="ej. Lunes y Miércoles 19:00"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Nivel</label>
                <input
                  type="text"
                  name="level"
                  value={formData.level}
                  onChange={handleInputChange}
                  placeholder="ej. Principiante, Intermedio, Avanzado"
                />
              </div>

              <div className="form-group">
                <label>Precio</label>
                <input
                  type="text"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="ej. $500/mes, $1200 por trimestre"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Instructor</label>
                <input
                  type="text"
                  name="instructor"
                  value={formData.instructor}
                  onChange={handleInputChange}
                  placeholder="Nombre del instructor"
                />
              </div>

              <div className="form-group">
                <label>Imagen del Instructor (URL)</label>
                <input
                  type="url"
                  name="instructorImage"
                  value={formData.instructorImage}
                  onChange={handleInputChange}
                  placeholder="https://ejemplo.com/foto-instructor.jpg"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Descripción</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Descripción detallada de la clase"
                rows="4"
              />
            </div>

            <div className="form-group">
              <label>Biografía del Instructor</label>
              <textarea
                name="instructorBio"
                value={formData.instructorBio}
                onChange={handleInputChange}
                placeholder="Breve biografía del instructor"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Qué traer/Sugerencias</label>
              <textarea
                name="whatToBring"
                value={formData.whatToBring}
                onChange={handleInputChange}
                placeholder="Materiales necesarios o sugerencias para los alumnos"
                rows="2"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Imagen de Fondo (URL)</label>
                <input
                  type="url"
                  name="backgroundImage"
                  value={formData.backgroundImage}
                  onChange={handleInputChange}
                  placeholder="https://ejemplo.com/fondo-clase.jpg"
                />
                <small className="field-hint">
                  URL de imagen para fondo personalizado
                </small>
              </div>

              <div className="form-group">
                <label>Clase de CSS (opcional)</label>
                <input
                  type="text"
                  name="image"
                  value={formData.image}
                  onChange={handleInputChange}
                  placeholder="dance, painting, yoga, etc."
                />
                <small className="field-hint">
                  Para color de fondo por defecto
                </small>
              </div>
            </div>

            <div className="form-group">
              <label>Galería de Imágenes</label>
              <div className="gallery-input">
                <input
                  type="url"
                  value={galleryInput}
                  onChange={handleGalleryInput}
                  placeholder="https://ejemplo.com/imagen-galeria.jpg"
                  className="gallery-url-input"
                />
                <button
                  type="button"
                  onClick={addGalleryImage}
                  className="add-gallery-btn"
                >
                  +
                </button>
              </div>
              <small className="field-hint">
                URLs de imágenes para la galería
              </small>

              {formData.gallery && formData.gallery.length > 0 && (
                <div className="gallery-preview">
                  <h4>Imágenes en galería:</h4>
                  <div className="gallery-list">
                    {formData.gallery.map((url, index) => (
                      <div key={index} className="gallery-item">
                        <span className="gallery-url">
                          {url.substring(0, 40)}...
                        </span>
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(index)}
                          className="remove-gallery-btn"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-btn">
                {editingClass ? "Actualizar Clase" : "Agregar Clase"}
              </button>
              {editingClass && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="cancel-btn"
                >
                  Cancelar
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Classes List */}
        <div className="classes-list-section">
          <h2>Todas las Clases ({classes.length})</h2>

          {loading ? (
            <div className="loading">Cargando clases...</div>
          ) : classes.length === 0 ? (
            <div className="no-classes">
              No se encontraron clases. Agrega tu primera clase arriba.
            </div>
          ) : (
            <div className="classes-table">
              <div className="table-header">
                <div className="table-cell">Título</div>
                <div className="table-cell">Horario</div>
                <div className="table-cell">Nivel</div>
                <div className="table-cell">Precio</div>
                <div className="table-cell">Instructor</div>
                <div className="table-cell actions">Acciones</div>
              </div>

              {classes.map((cls) => (
                <div key={cls.id} className="table-row">
                  <div className="table-cell title">{cls.title}</div>
                  <div className="table-cell schedule">{cls.schedule}</div>
                  <div className="table-cell level">{cls.level || "-"}</div>
                  <div className="table-cell price">{cls.price || "-"}</div>
                  <div className="table-cell instructor">
                    {cls.instructor || "-"}
                  </div>
                  <div className="table-cell actions">
                    <button
                      onClick={() => handleEdit(cls)}
                      className="edit-btn"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(cls.id)}
                      className="delete-btn"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClassesDashboard;
